import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ApexThemeService } from 'shared-ui';
import { MaintenanceWorkspaceService } from '../../core/services/maintenance-workspace.service';

type MaintenanceNavItem = {
  label: string;
  icon: string;
  route: string;
  permission?: string;
};

@Component({
  selector: 'app-maintenance-shell',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, RouterOutlet],
  templateUrl: './maintenance-shell.html',
  styleUrl: './maintenance-shell.scss',
})
export class MaintenanceShell {
  private readonly workspace = inject(MaintenanceWorkspaceService);
  private readonly theme = inject(ApexThemeService);
  protected readonly menuOpen = signal(false);

  private readonly allNavItems: MaintenanceNavItem[] = [
    { label: 'Dashboard', icon: 'dashboard', route: '/dashboard' },
    { label: 'Work Orders', icon: 'construction', route: '/work-orders', permission: 'MAINTENANCE_VIEW' },
    { label: 'Messages', icon: 'chat', route: '/communications', permission: 'COMMUNICATION_VIEW' },
    { label: 'Schedule', icon: 'calendar_month', route: '/schedule', permission: 'MAINTENANCE_VIEW' },
    { label: 'Inspections', icon: 'fact_check', route: '/inspections', permission: 'INSPECTION_VIEW' },
    { label: 'Parts & Costs', icon: 'receipt_long', route: '/parts', permission: 'MAINTENANCE_BILLING_MANAGE' },
    { label: 'Reports', icon: 'monitoring', route: '/reports', permission: 'REPORT_VIEW' },
  ];

  protected get navItems(): MaintenanceNavItem[] { return this.allNavItems.filter(item => !item.permission || this.workspace.allows(item.permission)); }

  protected readonly orgName = computed(() => this.workspace.displayName());

  protected get workspaces() { return this.workspace.visibleOrganizations(); }
  protected get currentOrgId(): string | null { return this.workspace.currentWorkspace()?.orgId ?? null; }

  /** Staff in more than one workspace switch here; a full load makes every page read the new workspace. */
  protected switchWorkspace(orgId: string): void {
    if (!orgId || orgId === this.currentOrgId) return;
    this.workspace.selectWorkspace(orgId);
    window.location.assign(`/dashboard?orgId=${encodeURIComponent(orgId)}`);
  }

  protected readonly initials = computed(() => {
    return this.orgName()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || 'MT';
  });

  /** Through the shared theme service, so PrimeNG, the theme classes and the stored choice all change together. */
  protected toggleTheme(): void {
    this.theme.toggleLightDark();
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected signOut(): void {
    void this.workspace.signOut();
  }
}
